import TickSquareSuccess from 'assets/icons/tick-square.svg';
import InfoSquareRed from 'assets/icons/info-square-red.svg';
import TimeCirclePending from 'assets/icons/time-circle-pending.svg';
import { NonprofitNomination, NonprofitNominationStatusType } from 'generated/graphql';
import {
  statusError,
  statusNeutral,
  statusSuccess,
  statusTextError,
  statusTextNeutral,
  statusTextSuccess,
} from 'utils/color-styles';

export type HeaderColumnInterface = {
  id: keyof NonprofitNomination | 'nominator';
  label: string;
  sorted: boolean;
  admin: boolean;
};

export enum NonprofitNominationInput {
  nonprofitLink = 'nonprofitLink',
}

export const NOMINATION_STATUS = {
  [NonprofitNominationStatusType.Submitted]: {
    name: 'Submitted',
    icon: TimeCirclePending,
    chipColor: statusNeutral,
    textColor: statusTextNeutral,
    copy: 'By updating this status to Submitted, this request will become active. The Nominator will NOT receive an email, but the status will be visible to the Nominator and Company Admin. Select Submit to update the status.',
  },
  [NonprofitNominationStatusType.Outreach]: {
    name: 'Outreach started',
    icon: TimeCirclePending,
    chipColor: statusNeutral,
    textColor: statusTextNeutral,
    copy: '',
  },
  [NonprofitNominationStatusType.NominationAccepted]: {
    name: 'Nomination Accepted',
    icon: TickSquareSuccess,
    inputs: ['nonprofitLink'],
    chipColor: statusSuccess,
    textColor: statusTextSuccess,
    copy: ' By updating this status to Nomination Accepted, the Nominator will receive an email notifying them that this Nonprofit is available in Deed and that their nomination is closed. Select Submit to update the status and notify this user.',
  },
  [NonprofitNominationStatusType.NominationDeclined]: {
    name: 'Nomination Declined',
    icon: InfoSquareRed,
    chipColor: statusError,
    textColor: statusTextError,
    copy: 'By updating this status to Nomination Declined, the Nominator will receive an email notifying them that this Nonprofit has declined this nomination and that their nomination is closed. Select Submit to update the status and notify this user.',
  },
  [NonprofitNominationStatusType.NoResponse]: {
    name: 'No Response',
    icon: InfoSquareRed,
    chipColor: statusError,
    textColor: statusTextError,
    copy: 'By updating this status to No Response, the Nominator will receive an email notifying them that this Nonprofit has not responded to this nomination and that their nomination is closed. Select Submit to update the status and notify this user.',
  },
  [NonprofitNominationStatusType.Onboarding]: {
    name: 'Onboarding',
    icon: TimeCirclePending,
    chipColor: statusNeutral,
    textColor: statusTextNeutral,
    copy: 'By updating this status to Onboarding, you will change the existing status. Do you wish to continue with this update?',
  },
  [NonprofitNominationStatusType.Troubleshooting]: {
    name: 'Troubleshooting',
    icon: InfoSquareRed,
    chipColor: statusError,
    textColor: statusTextError,
    copy: 'By updating this status to Troubleshooting, you will change the existing status. Do you wish to continue with this update?',
  },
  [NonprofitNominationStatusType.OrganizationFound]: {
    name: 'Organization Already Exists',
    icon: TickSquareSuccess,
    inputs: ['nonprofitLink'],
    chipColor: statusSuccess,
    textColor: statusTextSuccess,
    copy: 'By updating this status to Organization Already Exists, the Nominator will receive an email notifying them that this Nonprofit is already on the platform. Select Submit to update the status and notify this user.',
  },
  [NonprofitNominationStatusType.FilteredOut]: {
    name: 'Organization is being filtered out',
    icon: TickSquareSuccess,
    chipColor: statusSuccess,
    textColor: statusTextSuccess,
    copy: "By updating this status to Organization is being filtered out, the Nominator will receive an email notifying them that this Nonprofit is already on the platform but is being filtered out by their company's settings. Select Submit to update the status and notify this user.",
  },
};

export const COLUMNS: HeaderColumnInterface[] = [
  {
    id: 'nonprofitName',
    label: 'Nonprofit',
    sorted: true,
    admin: false,
  },
  {
    id: 'nonprofitEntityType',
    label: 'Type',
    sorted: true,
    admin: false,
  },
  {
    id: 'country',
    label: 'Country',
    sorted: true,
    admin: false,
  },
  {
    id: 'engagementType',
    label: 'Request',
    sorted: true,
    admin: false,
  },
  {
    id: 'otherNote',
    label: 'Request Comment',
    sorted: false,
    admin: false,
  },
  {
    id: 'email',
    label: 'Email',
    sorted: false,
    admin: false,
  },
  {
    id: 'ein',
    label: 'EIN',
    sorted: false,
    admin: false,
  },
  {
    id: 'ncesIdentifier',
    label: 'NCES ID',
    sorted: false,
    admin: false,
  },
  {
    id: 'phone',
    label: 'Phone',
    sorted: false,
    admin: false,
  },
  {
    id: 'createdAt',
    label: 'Submitted Date',
    sorted: true,
    admin: false,
  },
  {
    id: 'emails',
    label: 'Last Outreach Date',
    sorted: true,
    admin: false,
  },
  {
    id: 'nominator',
    label: 'Nominator',
    sorted: true,
    admin: false,
  },
  {
    id: 'Company',
    label: 'Company',
    sorted: true,
    admin: true,
  },
  {
    id: 'status',
    label: 'Status',
    sorted: true,
    admin: false,
  },
  {
    id: 'internalNote',
    label: 'Add Note',
    sorted: false,
    admin: true,
  },
];

export const OutreachLanguageCountry = [
  {
    label: 'USA - English',
    value: 'en-US',
  },
  {
    label: 'Australia - English',
    value: 'en-AU',
  },
  {
    label: 'Argentina - Spanish',
    value: 'es-AR',
  },
  {
    label: 'Austria - German',
    value: 'de-AT',
  },
  {
    label: 'Belgium - Dutch',
    value: 'nl-BE',
  },
  {
    label: 'Brazil - Portuguese',
    value: 'pt-BR',
  },
  {
    label: 'Bulgaria - Bulgarian',
    value: 'bg-BG',
  },
  {
    label: 'Canada - English',
    value: 'en-CA',
  },
  {
    label: 'Costa Rica - Spanish',
    value: 'es-CR',
  },
  {
    label: 'Cyprus - Greek',
    value: 'el-CY',
  },
  {
    label: 'Czech Republic - Czech',
    value: 'cs-CZ',
  },
  {
    label: 'Denmark - Danish',
    value: 'da-DK',
  },
  {
    label: 'Dominican Republic - Spanish',
    value: 'es-DO',
  },
  {
    label: 'France - French',
    value: 'fr-FR',
  },
  {
    label: 'Germany - German',
    value: 'de-DE',
  },
  {
    label: 'Greece - Greek',
    value: 'el-GR',
  },
  {
    label: 'Hungary - Hungarian',
    value: 'hu-HU',
  },
  {
    label: 'Ireland - English',
    value: 'en-IE',
  },
  {
    label: 'Italy - Italian',
    value: 'it-IT',
  },
  {
    label: 'Latvia - Latvian',
    value: 'lv-LV',
  },
  {
    label: 'Lithuania - Lithuanian',
    value: 'lt-LT',
  },
  {
    label: 'Luxembourg - French',
    value: 'fr-LU',
  },
  {
    label: 'Malta - Maltese',
    value: 'mt-MT',
  },
  {
    label: 'Mexico - Spanish',
    value: 'es-MX',
  },
  {
    label: 'Netherlands - Dutch',
    value: 'nl-NL',
  },
  {
    label: 'Norway - Norwegian',
    value: 'no-NO',
  },
  {
    label: 'Peru - Spanish',
    value: 'es-PE',
  },
  {
    label: 'Poland - Polish',
    value: 'pl-PL',
  },
  {
    label: 'Portugal - Portuguese',
    value: 'pt-PT',
  },
  {
    label: 'Slovakia - Slovak',
    value: 'sk-SK',
  },
  {
    label: 'Slovenia - Slovenian',
    value: 'sl-SI',
  },
  {
    label: 'Spain - Spanish',
    value: 'es-ES',
  },
  {
    label: 'Sweden - Swedish',
    value: 'sv-SE',
  },
  {
    label: 'Switzerland - German',
    value: 'de-CH',
  },
  {
    label: 'Great Britain - English',
    value: 'en-GB',
  },
  {
    label: 'Global Giving - English',
    value: 'en-globalGiving',
  },
];
